// Helpers compartidos del modulo RRHH (v49). Lo que usan las 4 pantallas:
// carga de listas, formato de valores y las etiquetas de completitud.

import axios from 'axios';
import { API } from '../config/api';

// Listas rrhh_* de ListasConfiguracion. El GET sin parametros devuelve TODAS las
// listas agrupadas ({ lista: [{id, valor, orden}] }) en una sola llamada; el
// backend ya excluye las rrhh_* para quien no es del grupo (listas.js §6.1).
// 'rrhh_tipo_formacion' ya no se usa: el tipo de registro de Formacion paso a
// ser una columna con CHECK (decision JC 1-ago), porque define estructura y no
// puede quedar a merced de que alguien renombre un valor de lista.
export const LISTAS_RRHH = [
  'rrhh_puesto', 'rrhh_departamento', 'rrhh_jornada', 'rrhh_modalidad_trabajo',
  'rrhh_estado_civil', 'rrhh_banco', 'rrhh_parentesco', 'rrhh_periodicidad_pago',
  'rrhh_estado_activo', 'rrhh_colegio_profesional', 'rrhh_grado_academico',
  'rrhh_tipo_licencia', 'rrhh_idioma', 'rrhh_nivel_idioma', 'rrhh_motivo_salida'
];

export async function cargarListas() {
  const r = await axios.get(`${API}/listas`);
  const todas = r.data || {};
  const out = {};
  LISTAS_RRHH.forEach(l => { out[l] = todas[l] || []; });
  return out;
}

// A1: cinco estados laborales. Vacaciones, Incapacitado y permisos NO son
// estado del expediente — los administraran sus propios modulos, y ponerlos aca
// crearia dos fuentes del mismo hecho.
//
// La spec decia 'Activo' para el estado de quien ya paso el periodo de prueba,
// pero chocaba de nombre con estado_expediente = 'Activo', que significa otra
// cosa. Como Activo/Inactivo es la convencion de todo el sistema para registros,
// el estado LABORAL es el que usa otra palabra: 'Laborando' (decision JC 1-ago).
export const ESTADOS_LABORALES = ['Preingreso', 'Periodo de prueba', 'Laborando', 'Suspendido', 'Terminado'];

// A2: tres tipos de contrato. Temporal y Servicios profesionales exigen fecha
// de vencimiento; Permanente no la lleva (§5.2).
export const TIPOS_CONTRATO = ['Servicios profesionales', 'Temporal', 'Permanente'];
export const CONTRATOS_CON_VENCIMIENTO = ['Temporal', 'Servicios profesionales'];

// Modalidades que exigen direccion de teletrabajo (§5.1).
export const MODALIDADES_REMOTAS = ['Teletrabajo', 'Híbrida'];

// Tipos de registro de Formacion: estructura, no catalogo. Cada uno muestra sus
// propios campos (§7.1) y el backend valida lo mismo.
export const TIPOS_FORMACION = [
  { id: 'Titulo',      valor: 'Título académico' },
  { id: 'Idioma',      valor: 'Idioma' },
  { id: 'Curso',       valor: 'Curso o certificación' },
  { id: 'Experiencia', valor: 'Experiencia previa' }
];
export const etiquetaFormacion = (t) => TIPOS_FORMACION.find(x => x.id === t)?.valor || t;

// Tipos de accion de personal (CK_AccPers_Tipo) y que campo cambia cada uno.
export const TIPOS_ACCION = ['Puesto', 'Salario', 'Jornada', 'Jefatura', 'Traslado', 'Contrato'];

// De que lista sale el valor_nuevo de cada tipo de accion. 'salario' y
// 'contrato' no salen de lista (numero y Temporal|Permanente).
export const LISTA_POR_TIPO = {
  Puesto:   'rrhh_puesto',
  Jornada:  'rrhh_jornada',
  Traslado: 'rrhh_departamento'
};

// Campo del expediente que refleja cada tipo (para precargar valor_anterior).
export const CAMPO_POR_TIPO = {
  Puesto:   { id: 'puesto_id',       texto: 'puesto' },
  Jornada:  { id: 'jornada_id',      texto: 'jornada' },
  Traslado: { id: 'departamento_id', texto: 'departamento' },
  Jefatura: { id: 'jefatura_id',     texto: 'jefatura_nombre' },
  Salario:  { id: 'salario',         texto: 'salario' },
  Contrato: { id: 'tipo_contrato',   texto: 'tipo_contrato' }
};

// Etiquetas legibles de los campos que devuelve campos_faltantes (D4).
export const ETIQUETA_CAMPO = {
  identificacion:      'Identificacion',
  nombre_completo:     'Nombre completo',
  fecha_ingreso:       'Fecha de ingreso',
  puesto:              'Puesto',
  estado_laboral:      'Estado laboral',
  salario:             'Salario',
  banco:               'Banco',
  cuenta_iban:         'Cuenta IBAN',
  contacto_emergencia: 'Contacto de emergencia'
};

// Valor para <input type="date">: YYYY-MM-DD sin pasar por new Date() (evita el
// shift de zona horaria que ya corrige utils/fecha.js para el display).
export const paraInputFecha = (v) => {
  if (!v) return '';
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

export const simboloMoneda = (m) => (m === 'Dólares' || m === 'Dolares') ? '$' : '₡';

export const fmtMonto = (n, moneda) => (n == null || n === '' || isNaN(n))
  ? '—'
  : `${simboloMoneda(moneda)}${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtBytes = (b) => {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

// Mensaje de error uniforme para axios.
export const msgError = (e) => e.response?.data?.error || e.message || 'Error inesperado';

// Texto legible del valor de una accion de personal. valor_nuevo se guarda como
// id de lista (Puesto/Jornada/Traslado), id de colaborador (Jefatura), numero
// (Salario) o literal (Contrato); valor_anterior se guarda ya en texto, asi que
// si no resuelve como id se devuelve tal cual.
export function textoValorAccion(tipo, valor, listas = {}, colaboradores = [], moneda) {
  if (valor === null || valor === undefined || valor === '') return null;
  if (tipo === 'Salario') return fmtMonto(valor, moneda);
  if (tipo === 'Contrato') return String(valor);
  if (tipo === 'Jefatura') {
    const c = colaboradores.find(x => String(x.id) === String(valor));
    return c ? c.nombre_completo : String(valor);
  }
  const lista = listas[LISTA_POR_TIPO[tipo]] || [];
  const hit = lista.find(x => String(x.id) === String(valor));
  return hit ? hit.valor : String(valor);
}

// ── Estilos compartidos del modulo (visual estandar del proyecto) ──────
export const inputStyle = {
  padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '8px',
  fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box',
  fontFamily: 'Segoe UI, sans-serif', background: '#fff'
};

export const btnPrimario = {
  padding: '8px 16px', background: '#87B749', color: '#fff', border: 'none',
  borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
};

export const btnSecundario = {
  padding: '8px 16px', background: '#fff', color: '#555', border: '1px solid #ddd',
  borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
};

export const btnNavy = {
  padding: '8px 20px', background: '#012E40', color: '#fff', border: 'none',
  borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
};

export const tarjeta = {
  background: '#fff', border: '1px solid #e8e8e8', borderRadius: '12px'
};

export const thStyle = {
  padding: '11px 14px', textAlign: 'left', color: '#888', fontWeight: '600',
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: '1px solid #eee'
};

export const tdStyle = { padding: '10px 14px', color: '#555' };
